import Button from "components/Button/Button";
import type { ButtonIconProps } from "components/ButtonIcon/ButtonIcon";
import Card from "components/Card/Card.tsx";
import type {
  DatePickerProps,
  OptionType,
  SelectProps,
} from "components/inputs";
import {
  Checkbox,
  DATE_TO_STRING,
  DatePicker,
  MultiSelect,
  Select,
  STRING_TO_DATE,
} from "components/inputs";
import type { FC } from "react";
import React, { useCallback } from "react";
import { Controller, useFormContext } from "react-hook-form";
import type { CardType, InputFields } from "types/FormTypes";
import { useDebouncedCallback } from "use-debounce";

import { normalizeValue } from "./form-utils.ts";

type KaboodleFormProps = {
  cards: Array<CardType | React.ReactNode>;
  CardComponent?: React.ElementType;
  classNames?: string;
  onSave: (field: string, value: string | number) => void;
  onDelete?: () => void;
  headerButton?: {
    hasButton: boolean;
  } & Partial<ButtonIconProps>;
  focusedField?: string | null;
};

const isCardType = (card: CardType | React.ReactNode): card is CardType => {
  return typeof card === "object" && card !== null && "fields" in card;
};

const KaboodleForm: React.FC<KaboodleFormProps> = ({
  cards,
  CardComponent = Card,
  classNames,
  onSave,
  onDelete,
  headerButton,
  focusedField,
}) => {
  const {
    control,
    formState: { errors },
    getValues,
    register,
  } = useFormContext();

  // use-debounce properly handles cleanup when component unmounts
  const debouncedSave = useDebouncedCallback(
    (field: string, value: unknown) => {
      // @ts-expect-error TODO: fix this
      onSave(field, normalizeValue(value));
    },
    500,
  );

  // only for components that need immediate saves (like DatePicker)
  const handleSave = useCallback(
    (field: string) => {
      const value = getValues(field);
      // @ts-expect-error TODO: fix this
      onSave(field, normalizeValue(value));
    },
    [onSave, getValues],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
      // prevent the form from submitting
      e.preventDefault();
    }
  };

  return (
    <div>
      <form
        className={classNames || "overview"}
        onKeyDown={handleKeyDown}
        autoComplete="off"
      >
        {cards.map((card, i) => {
          return !isCardType(card) ? (
            <React.Fragment key={i}>{card}</React.Fragment>
          ) : (
            <CardComponent
              key={i}
              heading={card.cardName}
              cardNumber={card.cardNumber}
              style={card.style}
              headerButton={headerButton}
              {...card.componentProps}
            >
              {card.fields.map((field) => {
                const Component = field.component;
                const options = (field.componentProps as SelectProps).options;
                const forceToNull = (
                  field.componentProps as { forceToNull?: boolean }
                )?.forceToNull;
                // eslint-disable-next-line no-console -- intentional dev-time invariant check
                console.assert(field.apiField, "apiField not set for ", field);

                const isFocused = focusedField === field.apiField;

                switch (Component.displayName) {
                  case "MultiSelect":
                    return (
                      <Controller
                        key={`${field.apiField}_${field.label}`}
                        control={control}
                        name={field.apiField || ""}
                        render={({
                          field: { onChange: onControllerChange, value },
                        }) => {
                          return (
                            <MultiSelect
                              label={field.label}
                              errors={errors}
                              options={options || []}
                              value={value}
                              onChange={(option) => {
                                onControllerChange(option);
                                debouncedSave(field.apiField || "", option);
                              }}
                              required={!!field.validation?.required}
                              {...(field.componentProps as SelectProps)}
                            />
                          );
                        }}
                      />
                    );

                  case "Select":
                    return (
                      <Controller
                        key={`${field.apiField}_${field.label}`}
                        control={control}
                        name={field.apiField || ""}
                        render={({
                          field: { onChange: onControllerChange, value },
                        }) => {
                          return (
                            <Select
                              label={field.label}
                              errors={errors}
                              options={options as OptionType[]}
                              value={
                                (options as OptionType[])?.find(
                                  (option) => option.value === value,
                                ) || undefined
                              }
                              onChange={(option) => {
                                onControllerChange(option);
                                debouncedSave(field.apiField || "", option);
                              }}
                              isFocused={isFocused}
                              required={!!field.validation?.required}
                              {...(field.componentProps as SelectProps)}
                            />
                          );
                        }}
                      />
                    );

                  case "DatePicker":
                    return (
                      <Controller
                        key={`${field.apiField}_${field.label}`}
                        control={control}
                        name={
                          field.apiField ||
                          `${field.label} apiField not yet set`
                        }
                        render={({ field: { value, onChange } }) => {
                          let moreProps = {};
                          if (value) {
                            moreProps = {
                              initialDate: STRING_TO_DATE(value),
                            };
                          }

                          return (
                            <DatePicker
                              key={value}
                              {...moreProps}
                              label={field.label}
                              onDateChange={(newDate) => {
                                const stringDate = DATE_TO_STRING(newDate);
                                onChange(stringDate);
                                handleSave(field.apiField || "");
                              }}
                              isFocused={isFocused}
                              errors={errors}
                              {...(field.componentProps as DatePickerProps)}
                            />
                          );
                        }}
                      />
                    );
                  case "Checkbox":
                    return (
                      <Checkbox
                        key={`${field.apiField}-${field.label}`}
                        label={field.label || ""}
                        isFocused={isFocused}
                        required={!!field.validation?.required}
                        {...register(field.apiField || "", {
                          onChange: () => {
                            // Checkbox needs immediate save for UX reasons
                            handleSave(field.apiField || "");
                          },
                        })}
                        {...(field.componentProps as Exclude<
                          InputFields,
                          FC<SelectProps> | FC<DatePickerProps>
                        >)}
                      />
                    );
                  default:
                    return (
                      <Controller
                        key={`${field.apiField}_${field.label}`}
                        control={control}
                        name={
                          field.apiField ||
                          `${field.label} apiField not yet set`
                        }
                        render={({
                          field: {
                            onChange: onControllerChange,
                            onBlur,
                            value,
                          },
                        }) => {
                          return (
                            <Component
                              key={`${field.apiField}-${field.label}`}
                              isFocused={isFocused}
                              name={
                                field.apiField ||
                                `${field.label} apiField not yet set`
                              }
                              label={field.label || ""}
                              value={value}
                              errors={errors}
                              required={!!field.validation?.required}
                              onChange={(e) => {
                                const newValue = forceToNull
                                  ? (e as React.ChangeEvent<HTMLInputElement>)
                                      .target.value || null
                                  : (e as React.ChangeEvent<HTMLInputElement>)
                                      .target.value;
                                onControllerChange(newValue);
                                debouncedSave(field.apiField || "", newValue);
                              }}
                              onBlur={onBlur}
                              // eslint-disable-next-line @typescript-eslint/no-explicit-any
                              {...(field.componentProps as any)}
                            />
                          );
                        }}
                      />
                    );
                }
              })}
            </CardComponent>
          );
        })}
      </form>
      {onDelete && <Button onClick={onDelete}>Delete</Button>}
    </div>
  );
};

export default KaboodleForm;
